import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Eye, 
  Edit, 
  Trash2, 
  Video,
  Loader2,
  Filter,
  Sparkles,
  Map,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const ProcessCaptures = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [captures, setCaptures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchCaptures();
  }, [statusFilter]);

  const fetchCaptures = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/process-captures?${params.toString()}`);
      setCaptures(response.data);
    } catch (error) {
      console.error('Failed to fetch captures:', error);
      toast.error('Failed to load process captures');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await api.delete(`/process-captures/${id}`);
      toast.success('Process capture deleted');
      fetchCaptures();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete process capture');
    }
  };

  const filteredCaptures = captures.filter(capture => 
    capture.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    capture.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    capture.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ready': return 'bg-emerald-100 text-emerald-700';
      case 'Draft': return 'bg-slate-100 text-slate-700';
      case 'Processing': return 'bg-blue-100 text-blue-700';
      case 'Archived': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="process-captures-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            Process Capture
          </h1>
          <p className="text-slate-500 mt-1">
            Record and document your processes with screen capture
          </p>
        </div>
        <Button 
          onClick={() => navigate('/process-capture/new')}
          className="bg-[#235894] hover:bg-[#1E4B80]"
          data-testid="start-capture-btn"
        >
          <Video className="w-4 h-4 mr-2" />
          Start New Capture
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search captures..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Filter className="w-4 h-4 mr-2" />
                  {statusFilter || 'All Status'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setStatusFilter('')}>
                  All Status
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Draft')}>
                  Draft
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Processing')}>
                  Processing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Ready')}>
                  Ready
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Archived')}>
                  Archived
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Captures List */}
      <Card>
        <CardContent className="p-0">
          {filteredCaptures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Video className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {captures.length === 0 ? 'No captures yet' : 'No matching captures'}
              </h3>
              <p className="text-slate-500 text-center max-w-md mb-4">
                {captures.length === 0 
                  ? 'Start recording your first process to create step-by-step documentation automatically.'
                  : 'Try adjusting your search or filters.'}
              </p>
              {captures.length === 0 && (
                <Button 
                  onClick={() => navigate('/process-capture/new')}
                  className="bg-[#235894] hover:bg-[#1E4B80]"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Start First Capture
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Steps</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCaptures.map((capture) => (
                  <TableRow 
                    key={capture.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/process-capture/${capture.id}`)}
                    data-testid={`capture-row-${capture.id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{capture.name}</p>
                        {capture.description && (
                          <p className="text-sm text-slate-500 truncate max-w-xs">
                            {capture.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{capture.department || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {capture.steps?.length || 0} steps
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Clock className="w-3 h-3" />
                        {formatDuration(capture.duration)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(capture.status)}>
                        {capture.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{capture.created_by_name}</TableCell>
                    <TableCell>
                      {new Date(capture.created_at).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/process-capture/${capture.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/process-capture/${capture.id}/edit`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {capture.status === 'Ready' && (
                            <DropdownMenuItem onClick={() => navigate(`/process-capture/${capture.id}?convert=true`)}>
                              <Map className="w-4 h-4 mr-2" />
                              Convert to Process Map
                            </DropdownMenuItem>
                          )}
                          {(isAdmin() || capture.created_by === user?.id) && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(capture.id, capture.name)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProcessCaptures;
